/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {defineMessages, defineMessage} from 'react-intl'
import {
    AccountIcon,
    LocationIcon,
    ReceiptIcon,
    HeartIcon,
} from '@salesforce/retail-react-app/app/components/icons'
import routingObject from '../../utils/static-routes'

export const messages = defineMessages({
    profile: {defaultMessage: 'Account Details', id: 'global.account.link.account_details'},
    addresses: {defaultMessage: 'Addresses', id: 'global.account.link.addresses'},
    orders: {defaultMessage: 'Order History', id: 'global.account.link.order_history'},
    wishlist: {defaultMessage: 'Wishlist', id: 'global.account.link.wishlist'},
})

export const accountMessages = defineMessages({
    personalInformation: {
        defaultMessage: 'Personal Information',
        id: 'account.heading.personal_information',
    },
    products: {defaultMessage: 'Products', id: 'account.heading.products'},
    yourPreferences: {defaultMessage: 'Your Preferences', id: 'account.heading.your_preferences'},
    yourAccount: {defaultMessage: 'Your Account', id: 'account.heading.your_account'},
    marketingPreferences: {
        defaultMessage: 'Marketing preferences',
        id: 'account.heading.marketing_preferences',
    },
    address: {defaultMessage: 'Address', id: 'account.heading.address'},
    paymentMethods: {defaultMessage: 'Payment methods', id: 'account.heading.payment_methods'},
    bringAFriend: {defaultMessage: 'Bring a friend', id: 'account.heading.bring_a_friend'},
    orderHistory: {defaultMessage: 'Order history', id: 'account.heading.order_history'},
    subscriptionHistory: {
        defaultMessage: 'Subscription history',
        id: 'account.heading.subscription_history',
    },
    registeredProducts: {
        defaultMessage: 'Your registered products',
        id: 'account.heading.registered_products',
    },
    wishlist: {defaultMessage: 'Wishlist', id: 'account.heading.wishlist'},
    repairTracking: {
        defaultMessage: 'Repair tracking tool',
        id: 'account.heading.repair_tracking_tool',
    },
})

export const ORDERS_HISTORY_SEARCH_PARAMS = {limit: 100, offset: 0}

export const navLinks = [
    {
        name: 'profile',
        path: '',
        icon: AccountIcon,
    },
    {
        name: 'wishlist',
        path: '/wishlist',
        icon: HeartIcon,
    },
    {
        name: 'orders',
        path: '/orders',
        icon: ReceiptIcon,
    },
    {
        name: 'addresses',
        path: '/addresses',
        icon: LocationIcon,
    },
]

export const accountNavLinks = [
    {
        title: 'personalInformation',
        links: [
            {name: 'yourAccount', path: ''},
            {name: 'marketingPreferences', path: '/marketing-preferences'},
            {name: 'address', path: '/addresses'},
            {name: 'paymentMethods', path: '/payment-methods'},
            {name: 'bringAFriend', path: routingObject.REFERRAL_DASHBOARD_PATH},
        ],
    },
    {
        title: 'products',
        links: [
            {name: 'orderHistory', path: '/orders'},
            {name: 'subscriptionHistory', path: '/subscription-history'},
            {name: 'registeredProducts', path: '/registered-products'},
            {name: 'repairTracking', path: '/repair-tracking-tool'},
        ],
    },
    {
        title: 'yourPreferences',
        links: [{name: 'wishlist', path: '/wishlist'}],
    },
]

export const CONFIRMATION_DIALOG_DEFAULT_CONFIG = defineMessages({
    dialogTitle: {
        defaultMessage: 'Confirm Action',
        id: 'confirmation_modal.default.title.confirm_action',
    },
    confirmationMessage: {
        defaultMessage: 'Are you sure you want to continue?',
        id: 'confirmation_modal.default.message.you_want_to_continue',
    },
    primaryActionLabel: {defaultMessage: 'Yes', id: 'confirmation_modal.default.action.yes'},
    primaryActionAriaLabel: {
        defaultMessage: 'Yes, confirm action',
        id: 'confirmation_modal.default.assistive_msg.yes',
    },
    alternateActionLabel: {defaultMessage: 'No', id: 'confirmation_modal.default.action.no'},
    alternateActionAriaLabel: {
        defaultMessage: 'No, cancel action',
        id: 'confirmation_modal.default.assistive_msg.no',
    },
})

export const SFCC_ORDER_STATUS = {
    CANCELLED: 'canceled',
    PROCESSING: 'processing',
    PARTIALLY_SHIPPED: 'partially shipped',
    PARTIALLY_DELIVERED: 'partially delivered',
    SHIPPED: 'shipped',
    DELIVERED: 'delivered',
    RETURN_REQUESTED: 'return requested',
    COMPLETED: 'completed',
    REJECTED: 'rejected',
    CREATED: 'created',
    IN_PREPARATION: 'in preparation',
    REQUESTED: 'requested',
    CLOSED: 'closed',
    DECLINED: 'declined',
    INVETORY_CHECK: 'inventory check',
    INVENTORY_UNAVAILABLE: 'inventory unavailable',
    PAYMENT_AUTHORIZATION: 'payment authorization',
    PAYMENT_FAILED: 'payment failed',
    FAILED: 'failed',
}

export const orderStatusMapping = {
    simple: [
        {
            sfcc: SFCC_ORDER_STATUS.PROCESSING,
            oms: [
                'created',
                'grace period',
                'vat check',
                'waiting to fulfill',
                'processing',
                'in preparation',
                'exported',
                'inventory check',
                'payment authorization',
            ],
            message: defineMessage({
                defaultMessage: 'Processing',
                id: 'status.processing',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.CANCELLED,
            oms: ['export error', 'canceled'],
            message: defineMessage({
                defaultMessage: 'Canceled',
                id: 'status.canceled',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.FAILED,
            oms: ['inventory unavailable', 'payment failed'],
            message: defineMessage({
                defaultMessage: 'Failed',
                id: 'status.failed',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.PARTIALLY_SHIPPED,
            oms: ['partially shipped'],
            message: defineMessage({
                defaultMessage: 'Partially shipped',
                id: 'status.partially_shipped',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.PARTIALLY_DELIVERED,
            oms: ['partially delivered'],
            message: defineMessage({
                defaultMessage: 'Partially delivered',
                id: 'status.partially_delivered',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.SHIPPED,
            oms: ['shipped'],
            message: defineMessage({
                defaultMessage: 'Shipped',
                id: 'status.shipped',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.DELIVERED,
            oms: ['delivered', 'closed'],
            message: defineMessage({
                defaultMessage: 'Delivered',
                id: 'status.delivered',
            }),
        },
    ],
    return: [
        {
            sfcc: SFCC_ORDER_STATUS.RETURN_REQUESTED,
            oms: [
                'created',
                'ready for export',
                'export error',
                'processing',
                'return to warehouse',
                'lost package',
                'returned at warehouse',
            ],
            message: defineMessage({
                defaultMessage: 'Return requested',
                id: 'status.return_requested',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.COMPLETED,
            oms: ['completed', 'partially completed', 'declined'],
            message: defineMessage({
                defaultMessage: 'Completed',
                id: 'status.completed',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.REJECTED,
            oms: ['non-conform', 'non-compliant'],
            message: defineMessage({
                defaultMessage: 'Rejected',
                id: 'status.rejected',
            }),
        },
    ],
    fullfillment: [
        {
            sfcc: SFCC_ORDER_STATUS.CREATED,
            oms: ['created'],
            message: defineMessage({
                defaultMessage: 'Created',
                id: 'status.created',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.IN_PREPARATION,
            oms: ['in preparation'],
            message: defineMessage({
                defaultMessage: 'In Preparation',
                id: 'status.in_preparation',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.SHIPPED,
            oms: ['shipped'],
            message: defineMessage({
                defaultMessage: 'Shipped',
                id: 'status.shipped',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.DELIVERED,
            oms: ['delivered'],
            message: defineMessage({
                defaultMessage: 'Delivered',
                id: 'status.delivered',
            }),
        },
        {
            sfcc: SFCC_ORDER_STATUS.CANCELLED,
            oms: ['canceled'],
            message: defineMessage({
                defaultMessage: 'Canceled',
                id: 'status.canceled',
            }),
        },
    ],
}
